import React, { useState, useEffect, forwardRef, useRef } from 'react';
import { useMatch } from 'react-router-dom';
import classNames from 'classnames';

import List from '@sv-support-sdk/ui-components/src/components/Lists/List';

import Button from '../Button';
import LoaderFallback from '../../Feedback/Loader/LoaderFallback';
import SearchResult from '../../../containers/Search/components/SearchResult';

import { useAppSelector } from '../../../store/hooks';
import { selectDropdownResultList } from '../../../store/selectors/search';
import { formResultLink } from '../../../helpers/links';
import { routes } from '../../../routing';
import { $l } from '../../../locales';

import css from './style.module.scss';
import setQaAttribute from '../../../helpers/setQaAttribute';
import {
  SUPPORT_HEADER_SEARCH_CLEAR,
  SUPPORT_HEADER_SEARCH_INPUT,
  SUPPORT_HEADER_SEARCH_RESULT,
  SUPPORT_SEATCH_SHOW_MORE,
} from '../../../constants/qaAttributes';

interface IProps {
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleInputKeyUp: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  clearSearchValue: () => void;
  goBack: () => void;
  showDropdown: boolean;
  handleSearchAllResults: () => void;
  searchValue: string;
  loading: boolean;
  isSearchOpened?: boolean;
}

const headerHeight = 56;
const allSearchHeight = 48;

const MobileSearch = forwardRef<HTMLInputElement, IProps>(function MobileSearch(
  {
    handleInputChange,
    handleInputKeyUp,
    clearSearchValue,
    goBack,
    showDropdown,
    handleSearchAllResults,
    searchValue,
    loading,
    isSearchOpened,
  },
  ref,
) {
  const isSearchRoute = useMatch(routes.search);
  const searchRef = useRef<HTMLDivElement>(null);
  const dropdownList = useAppSelector(selectDropdownResultList);
  const [resultsHeight, setResultsHeight] = useState(
    window.visualViewport?.height ?? 0 - headerHeight - allSearchHeight,
  );
  const isIos = /iPhone|iPad|iPod/.test(window.navigator.userAgent);

  useEffect(() => {
    if (!isIos) return;

    function scrollHandler() {
      if (isSearchOpened) {
        window.scrollTo(0, 0);
      }
    }

    function resizeHandler() {
      const viewportHeight = window.visualViewport?.height ?? 0;
      setResultsHeight(viewportHeight - headerHeight - allSearchHeight);
    }

    resizeHandler();

    window.visualViewport?.addEventListener('scroll', scrollHandler);
    window.visualViewport?.addEventListener('resize', resizeHandler);
    document.addEventListener('touchend', scrollHandler);

    return () => {
      window.visualViewport?.removeEventListener('scroll', scrollHandler);
      window.visualViewport?.removeEventListener('resize', resizeHandler);
      document.removeEventListener('touchend', scrollHandler);
    };
  }, [isSearchOpened]);

  return (
    <div
      ref={searchRef}
      className={classNames(css.mobileSearch, { [css.isSearchRoute]: isSearchRoute })}
    >
      <div className={css.mobileSearch__control}>
        <div className={css.mobileSearch__backButton}>
          <Button iconSize="24" icon="24--chevron-left" variant="icon" noHover onClick={goBack} />
        </div>
        <div className={css.mobileSearch__inputWrapper}>
          <input
            ref={ref}
            value={searchValue}
            placeholder={$l('SEARCH.SEARCH_HELP_CENTER')}
            onChange={handleInputChange}
            onKeyUp={handleInputKeyUp}
            className={css.mobileSearch__input}
            autoFocus={!isSearchRoute}
            {...setQaAttribute(SUPPORT_HEADER_SEARCH_INPUT)}
          />
        </div>
        {searchValue.trim() ? (
          <div className={css.mobileSearch__clearButton}>
            <Button
              iconSize="24"
              icon="24--close"
              variant="icon"
              noHover
              {...setQaAttribute(SUPPORT_HEADER_SEARCH_CLEAR)}
              onClick={clearSearchValue}
            />
          </div>
        ) : null}
      </div>
      {showDropdown && (
        <>
          {loading && (
            <div className={css.mobileSearch__loader}>
              <LoaderFallback size="md" />
            </div>
          )}
          {!loading && !dropdownList.length && (
            <div
              {...setQaAttribute(SUPPORT_HEADER_SEARCH_RESULT)}
              className={css.mobileSearch__noResults}
            >
              {$l('SEARCH.NO_RESULTS')}
            </div>
          )}
          {!loading && dropdownList.length > 0 && (
            <>
              <div
                className={css.mobileSearch__results}
                style={isIos ? { height: `${resultsHeight}px` } : {}}
                {...setQaAttribute(SUPPORT_HEADER_SEARCH_RESULT)}
              >
                {dropdownList.length ? (
                  <List className={classNames(css.results__list, css.isMobile)}>
                    {dropdownList.map((result) => (
                      <SearchResult
                        key={`${result.type}-${result.slug}`}
                        type={result.type}
                        description={result.description}
                        title={result.title}
                        isMobile
                        // @ts-expect-error - expected error
                        link={formResultLink(result)}
                      />
                    ))}
                  </List>
                ) : null}
              </div>
              <div className={css.mobileSearch__all}>
                <Button
                  iconSize="24"
                  icon="24--search"
                  variant="transparent"
                  radius="0"
                  text={$l('SEARCH.SEE_ALL_SEARCH_RESULTS')}
                  {...setQaAttribute(SUPPORT_SEATCH_SHOW_MORE)}
                  onClick={handleSearchAllResults}
                />
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
});

export default MobileSearch;
